import { useTranslation } from 'react-i18next';
import { Info, Layers, SignpostBig, User } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { BOARD_GROUPINGS, type BoardGrouping } from './board-utils';

interface BoardGroupingToggleProps {
  value: BoardGrouping;
  onChange: (grouping: BoardGrouping) => void;
}

const ICONS: Record<BoardGrouping, typeof Layers> = {
  zone: SignpostBig,
  setter: User,
  grade: Layers,
};

/**
 * What the columns stand for — one choice at a time, since a column can only cut
 * the board one way. Radix keeps the pressed item's `data-state` for us; the
 * styling below is the segmented control the rest of the admin uses.
 *
 * Cards can be dragged between columns whichever cut is showing, so what a drop
 * will do when it is not just sorting into a zone hangs off the info icon rather
 * than off a line of text the toolbar has no room for.
 */
export function BoardGroupingToggle({ value, onChange }: BoardGroupingToggleProps) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-1.5">
      <ToggleGroup
        type="single"
        value={value}
        // Radix reports an empty value when the pressed item is pressed again;
        // the board always has a grouping, so that is a no-op rather than none.
        onValueChange={(next) => {
          if (next) onChange(next as BoardGrouping);
        }}
        aria-label={t('gymAdmin.routeSettingBoard.groupBy.label')}
        className="inline-flex h-8 items-center gap-1 rounded-md border bg-muted/40 p-0.5"
        data-testid="board-grouping-toggle"
      >
        {BOARD_GROUPINGS.map((grouping) => {
          const Icon = ICONS[grouping];

          return (
            <ToggleGroupItem
              key={grouping}
              value={grouping}
              data-testid={`board-grouping-${grouping}`}
              className="inline-flex h-7 items-center gap-1.5 rounded px-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
            >
              <Icon className="size-3.5" aria-hidden />
              {t(`gymAdmin.routeSettingBoard.groupBy.${grouping}`)}
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>

      {value !== 'zone' && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Info
              className="size-3.5 shrink-0 text-muted-foreground"
              data-testid="board-grouping-hint"
              aria-label={t(`gymAdmin.routeSettingBoard.groupBy.dragHint.${value}`)}
            />
          </TooltipTrigger>
          <TooltipContent className="max-w-64">
            {t(`gymAdmin.routeSettingBoard.groupBy.dragHint.${value}`)}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
